import {
    BookmarkCheck,
    Briefcase,
    Building,
    CreditCard,
    Gift,
    Home,
    MessageSquare, Plus, Search,
    Settings,
    User,
    X,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../store/authStore.js";

// ─── Nav definitions per role ─────────────────────────────────────────────────
const PROFESSIONAL_NAV = [
    { name: "Dashboard", path: "/dashboard", icon: Home },
    { name: "Find Jobs", path: "/job-search", icon: Briefcase },
    { name: "Applications", path: "/my-applications", icon: MessageSquare },
    { name: "Recommendations", path: "/professional/recommendations", icon: Gift },
    { name: "Saved", path: "/saved-jobs", icon: BookmarkCheck },
    { name: "Profile", path: "/manage-profile", icon: User },
    { name: "Pricing", path: "/pricing", icon: CreditCard },
    { name: "Settings", path: "/account-settings", icon: Settings },
];

const RECRUITER_NAV = [
    { name: "Dashboard", path: "/dashboard", icon: Home },
    { name: "Post Job", path: "/post-job", icon: Plus },
    { name: "Manage Jobs", path: "/manage-jobs", icon: Briefcase },
    { name: "Applications", path: "/view-applications/all", icon: MessageSquare },
    { name: "Recommendations", path: "/recruiter/recommendations", icon: Gift },
    { name: "Find Talent", path: "/find-professionals", icon: Search },
    { name: "Company", path: "/company-profile", icon: Building },
    { name: "Pricing", path: "/pricing", icon: CreditCard },
    { name: "Settings", path: "/account-settings", icon: Settings },
];

// ─── Single nav item ──────────────────────────────────────────────────────────
const NavItem = ({ item, isActive, onClick }) => {
    const Icon = item.icon;
    return (
        <button
            onClick={onClick}
            title={item.name}
            className={`
        w-full flex flex-col items-center justify-center gap-1 py-3 px-1 rounded-xl
        transition-all duration-200 relative
        ${isActive
                    ? "bg-white/20 text-white"
                    : "text-blue-200 hover:bg-white/10 hover:text-white"
                }
      `}
        >
            {isActive && (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full" />
            )}
            <Icon size={20} strokeWidth={isActive ? 2.5 : 1.8} />
            <span className="text-[9px] font-semibold tracking-wide leading-none">{item.name}</span>
        </button>
    );
};

// ─── Main sidebar ─────────────────────────────────────────────────────────────
const DashboardSidebar = ({ isMobileOpen, onMobileClose }) => {
    const authUser = useAuthStore(s => s.user);
    const navigate = useNavigate();
    const location = useLocation();

    // const { data: user } = useQuery({
    //     queryKey: ["profile"],
    //     queryFn: async () => {
    //         const res = await authServiceApi.getProfile();
    //         return res.data;
    //     },
    // });

    const navItems =
        (authUser?.role) === "recruiter"
            ? RECRUITER_NAV
            : PROFESSIONAL_NAV;

    const isActive = (path) =>
        path === "/dashboard"
            ? location.pathname === path
            : location.pathname.startsWith(path);

    const handleNav = (path) => {
        navigate(path);
        onMobileClose?.();
    };

    const sidebarContent = (
        <aside
            className="h-screen w-[88px] bg-[#1e3a5f] flex flex-col items-center pt-3 pb-4 shadow-2xl"
            style={{ minWidth: 88 }}
        >
            {/* Divider */}
            <div className="w-8 border-t border-blue-700 mb-2 mt-1" />

            {/* Nav items — scrollable */}
            <nav className="flex-1 w-full overflow-y-auto px-2 space-y-1 scrollbar-none">
                {navItems.map((item) => (
                    <NavItem
                        key={item.path}
                        item={item}
                        isActive={isActive(item.path)}
                        onClick={() => handleNav(item.path)}
                    />
                ))}
            </nav>
        </aside>
    );

    return (
        <>
            {/* Desktop: fixed left sidebar — starts below the top bar (h-14 = 56px) */}
            <div className="hidden lg:block fixed left-0 top-14 z-40 bottom-0">
                {sidebarContent}
            </div>

            {/* Mobile drawer */}
            {isMobileOpen && (
                <div className="lg:hidden fixed inset-0 z-50 flex">
                    <div className="relative">{sidebarContent}</div>
                    <div
                        className="flex-1 bg-black/50 backdrop-blur-sm"
                        onClick={onMobileClose}
                    />
                    <button
                        className="absolute top-4 left-[96px] text-white bg-blue-800 rounded-full p-1 shadow"
                        onClick={onMobileClose}
                    >
                        <X size={16} />
                    </button>
                </div>
            )}
        </>
    );
};

export default DashboardSidebar;
